package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.agent.AgentClient
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** The value is an existing localization workflow, not a claim that coding agents cannot translate. */
@Composable
fun AgentValueBand() {
    Band(ground = BandGround.PaperSunken, labelledBy = "agent-value-title") {
        Div(Modifier.gap(Space.MD).styleModifier { property("display", "flex") }.toAttrs()) {
            listOf(AgentClient.CLAUDE_CODE, AgentClient.CODEX, AgentClient.CURSOR).forEach { AgentClientLogo(it) }
        }
        BandHeading(t("marketing.agent_value.agent_drafts_team_reviews", "Your agent drafts. Your team reviews."), id = "agent-value-title")
        DocsProse(t("marketing.agent_value.shared_glossary_context_status", "Your coding agent can draft translations. Lingvo keeps your glossary, string context and translation status together."))
        DocsProse(t("marketing.agent_value.review_then_approve", "You or your team review the suggestions and approve translations separately."))
        PublicActionLink("/agents#why-lingvo", t("marketing.agent_value.explore", "See what Lingvo adds"))
    }
}

@Composable
fun AgentValueComparison() {
    DocsProse(t("marketing.agent_value.honest_baseline", "You can ask Claude Code to translate your files today. It can read project instructions, use skills, run your checks and open a pull request. If that already covers your localization workflow, you may not need another service."))
    DocsProse(t("marketing.agent_value.core_value", "Lingvo is useful when localization becomes a recurring release task. It gives your agents and your browser workflow the same stored project context, translation state and QA results. The value is the workflow you can reuse and maintain across releases."))
    LTable(
        caption = t("marketing.agent_value.comparison_caption", "An agent in your repository, with and without Lingvo"),
        captionHidden = true,
        tableModifier = AgentValueTableStyle.toModifier().attr("role", "table")
            .styleModifier { property("min-width", "0") },
    ) {
        LTableHead(AgentValueHeadStyle.toModifier().attr("role", "rowgroup")) {
            LTableRow {
                LTableHeaderCell { Text(t("marketing.agent_value.job", "The job")) }
                LTableHeaderCell { Text(t("marketing.agent_value.agent_only", "Agent + your repository")) }
                LTableHeaderCell { Text(t("marketing.agent_value.with_lingvo", "What Lingvo provides")) }
            }
        }
        LTableBody(AgentValueBodyStyle.toModifier().attr("role", "rowgroup")) {
            ComparisonRow(
                t("marketing.agent_value.context_job", "Keep wording consistent"),
                t("marketing.agent_value.context_diy", "Keep glossary and instructions in files, and build them into your prompts or skills."),
                t("marketing.agent_value.context_service", "A stored style guide and glossary, assembled into a translation brief with string context, placeholders, length limits and target-language plural categories."),
            )
            ComparisonRow(
                t("marketing.agent_value.review_job", "Review the suggestions"),
                t("marketing.agent_value.review_diy", "Review the file diff or pull request using your team’s conventions."),
                t("marketing.agent_value.review_service", "A proposal queue beside the current source and translation. Accepting a proposal creates a translated entry; approving it is a separate decision. Pending proposals stay out of exports."),
            )
            ComparisonRow(
                t("marketing.agent_value.qa_job", "Check every release"),
                t("marketing.agent_value.qa_diy", "Write and maintain scripts or choose localization validators, then wire them into CI."),
                t("marketing.agent_value.qa_service", "Repeatable checks for supported placeholders, plural forms and configured constraints, plus per-language readiness. The same QA findings are available in the browser, CLI and MCP."),
            )
            ComparisonRow(
                t("marketing.agent_value.roundtrip_job", "Keep the repository in sync"),
                t("marketing.agent_value.roundtrip_diy", "Have the agent edit each format, and maintain your own import, export and conflict handling."),
                t("marketing.agent_value.roundtrip_service", "A configured push/pull workflow for supported localization formats, with previews, conflict checks and a QA command that can fail your build."),
            )
        }
    }
    DocsProse {
        DocsInlineLink("https://code.claude.com/docs/en/overview", t("marketing.agent_value.claude_source", "Claude Code’s own documentation covers file editing, skills, tests and translation in CI."))
    }
}

@Composable
fun AgentValueExample() {
    DocsProse(t("marketing.agent_value.example", "Suppose you change the wording of a checkout button. Your agent can translate the new text. With Lingvo, you can request a brief for translations whose source has changed, include your preferred terminology, collect proposals for review, check each locale and pull the resulting translations into the app."))
    DocsCode("lingvo push --dry-run\nlingvo push\n# Request proposals and review them in Lingvo.\nlingvo qa --fail-on-error\nlingvo pull --dry-run\nlingvo pull")
    DocsProse(t("marketing.agent_value.limits", "Structural QA cannot guarantee a natural translation or catch every runtime defect. Keep language review, app tests and your release checks. Lingvo’s MCP permissions also do not restrict an agent’s independent shell access."))
}

@Composable
fun AgentValueFit() {
    Column(Modifier.gap(Space.MD)) {
        DocsSubheading(t("marketing.agent_value.enough_title", "An agent alone may be enough"))
        DocsProse(t("marketing.agent_value.enough_body", "You need a one-off translation, have a small set of locale files, or already have glossary, review and validation working well in Git. Keep that workflow if it serves you."))
        DocsSubheading(t("marketing.agent_value.fit_title", "Add Lingvo when the workflow starts repeating"))
        DocsProse(t("marketing.agent_value.fit_body", "You ship language changes regularly, need a clear queue of translations to review, or want several people and agents to work from the same project state. Lingvo is a choice to use a maintained localization workflow instead of assembling all of it yourself."))
        DocsSubheading(t("marketing.agent_value.pay_title", "What you pay for"))
        DocsProse(t("marketing.agent_value.pay_body", "Start on Free. Basic agent access, manual review, QA and your own provider keys remain available. Paid plans expand project and owner/admin seat limits and include more platform machine-translation credits. Reading a brief or submitting a proposal does not spend Lingvo MT credits; your agent’s own usage charges still apply."))
        DocsProse { DocsInlineLink("/pricing", t("marketing.agent_value.pricing", "Compare the current plans and limits")) }
    }
}

@Composable
private fun ComparisonRow(job: String, repository: String, lingvo: String) {
    LTableRow(AgentValueRowStyle.toModifier().attr("role", "row")) {
        LTableHeaderCell(AgentValueCellStyle.toModifier().attr("role", "rowheader"), scope = LTableHeaderScope.ROW) { Text(job) }
        LTableCell(AgentValueCellStyle.toModifier().attr("role", "cell")) {
            Span(AgentValueCellLabelStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
                Text(t("marketing.agent_value.agent_only", "Agent + your repository"))
            }
            Text(repository)
        }
        LTableCell(AgentValueCellStyle.toModifier().attr("role", "cell")) {
            Span(AgentValueCellLabelStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
                Text(t("marketing.agent_value.with_lingvo", "What Lingvo provides"))
            }
            Text(lingvo)
        }
    }
}
